// src/components/PostScheduling.js
import { useEffect, useRef, useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import FacebookIcon from "./../../../../assets/img/marks/facebook.png";
import InstagramIcon from "./../../../../assets/img/marks/instagram.png";
import TiktokIcon from "./../../../../assets/img/marks/tiktok.png";
import TwitterIcon from "./../../../../assets/img/marks/twitter.png";
import { axiosApi } from "config";
import { serverUrl } from "config";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PostScheduling = () => {
  const [timeZone, setTimeZone] = useState("(GMT+1)");
  const [editTimeZone, setEditTimeZone] = useState(false);
  const timeZoneRef = useRef(null);
  const [editMode, setEditMode] = useState(Array(4).fill(false));
  const [platformTimes, setPlatformTimes] = useState([
    ["07:00 am", "12:00 pm", "16:00 pm"],
    ["07:00 am", "12:00 pm", "16:00 pm"],
    ["07:00 am", "12:00 pm", "16:00 pm"],
    ["07:00 am", "12:00 pm", "16:00 pm"],
  ]);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const navigator = useNavigate();

  const socialPlatforms = [
    {
      name: "Facebook",
      icon: FacebookIcon,
      color: "text-blue-600",
    },
    {
      name: "Instagram",
      icon: InstagramIcon,
      color: "text-pink-600",
    },
    {
      name: "TikTok",
      icon: TiktokIcon,
      color: "text-black",
    },
    {
      name: "Twitter",
      icon: TwitterIcon,
      color: "text-blue-400",
    },
  ];

  useEffect(() => {
    // Fetch connected social accounts on component mount
    fetchConnectedAccounts();

    // Listen for messages from popup
    window.addEventListener("message", handleMessage, false);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("message", handleMessage, false);
    };
  }, []);

  const fetchConnectedAccounts = async () => {
    try {
      const response = await axiosApi.get("/user/info", {
        withCredentials: true,
      });
      setConnectedAccounts(response.data.platforms);
    } catch (error) {
      console.error("Error fetching connected accounts:", error.response?.data || error.message);
      toast.error("Failed to fetch connected accounts.");
    }
  };

  const handleAddAccount = (platformName) => {
    const url = `${serverUrl}/api/social-accounts/${platformName.toLowerCase()}/login`;

    // Open a small popup window
    const popup = window.open(url, "OAuthPopup", "width=600,height=600");

    if (!popup) {
      toast.error("Popup blocked! Please allow popups for this website.");
      return;
    }
  };

  const handleMessage = (event) => {
    // if (event.origin !== process.env.REACT_APP_BACKEND_URL) return;

    const { data } = event;

    if (data === "oauth-success") {
      toast.success("Successfully connected the account!");
      fetchConnectedAccounts();
    } else if (data === "oauth-error") {
      toast.error("Failed to connect the account.");
    }
  };

  const handleRemoveAccount = async (platformId) => {
    try {
      await axiosApi.delete("/user/delete_socialAccount", {
        data: { platform_id: platformId }, // Assuming platformId is the SocialAccount's ID
        withCredentials: true,
      });
      // Refresh the connected accounts
      fetchConnectedAccounts();
      toast.success("Social account removed successfully.");
    } catch (error) {
      console.error("Error removing social account:", error.response?.data || error.message);
      toast.error("Failed to remove social account.");
    }
  };

  // Handle query parameters to show success/error messages after OAuth redirects (optional)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');

    if (success) {
      toast.success(`Successfully connected to ${capitalizeFirstLetter(success)}!`);
      fetchConnectedAccounts();
      navigator.replace('/main/profile'); // Remove query params from URL
    }

    if (error) {
      toast.error(`Failed to connect to ${capitalizeFirstLetter(error)}.`);
      navigator.replace('/main/profile'); // Remove query params from URL
    }
  }, [navigator]);

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleTimeZoneEdit = () => {
    setEditTimeZone(!editTimeZone);
  };

  const toggleEditMode = (index) => {
    const newEditMode = [...editMode];
    newEditMode[index] = !newEditMode[index];
    setEditMode(newEditMode);
  };

  const handleTimeChange = (platformIndex, timeIndex, newTime) => {
    const newPlatformTimes = [...platformTimes];
    newPlatformTimes[platformIndex][timeIndex] = newTime;
    setPlatformTimes(newPlatformTimes);
  };

  return (
    <div className="mt-4">
      <Link to="/main/profile">
        <div className="flex items-center gap-4 font-bold text-purple-800 hover:text-purple-500">
          <MdArrowBack className="flex h-4 w-4" />
          <p>Back to Settings</p>
        </div>
      </Link>

      <div className="mt-8 space-y-8">
        {/* Social Accounts Section */}
        <div className="rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-6 shadow-md">
          <h4 className="text-lg font-semibold">Social Accounts</h4>
          <div className="mt-6 flex items-start justify-between">
            <div className="flex flex-col items-start gap-4">
              <p className="font-medium text-gray-800">
                Connected Social Accounts
              </p>
              <div className="flex flex-wrap gap-2">
                {connectedAccounts.length > 0 ? (
                  connectedAccounts.map((account) => (
                    <div key={account._id} className="flex items-center gap-2">
                      <img
                        src={
                          account.platform === 'Facebook'
                            ? FacebookIcon
                            : account.platform === 'Instagram'
                            ? InstagramIcon
                            : account.platform === 'TikTok'
                            ? TiktokIcon
                            : TwitterIcon
                        }
                        alt={account.platform}
                        className="h-8 w-8 rounded-full"
                      />
                      <span>{account.accountName}</span>
                      <button
                        className="text-red-500 hover:text-red-700 ml-2"
                        onClick={() => handleRemoveAccount(account._id)}
                        title={`Remove ${account.platform}`}
                      >
                        &times;
                      </button>
                    </div>
                  ))
                ) : (
                  <p>No connected social accounts.</p>
                )}
              </div>
            </div>
            <div>
              <p className="font-medium text-gray-800 mb-2">Add Social Account</p>
              <div className="flex space-x-4">
                {socialPlatforms.map((platform) => (
                  <button
                    key={platform.name}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md bg-${platform.color} bg-opacity-20 hover:bg-opacity-30`}
                    onClick={() => handleAddAccount(platform.name)}
                  >
                    <img src={platform.icon} alt={platform.name} className="h-6 w-6" />
                    <span className={`text-${platform.color.replace('text-', '')}`}>{platform.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Time Zone Settings Section */}
        <div className="rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-6 shadow-md">
          <h4 className="text-lg font-semibold">
            Accounts and Time Zone Settings
          </h4>
          <div className="mt-6 flex justify-between">
            <div className="w-full max-w-[300px]">
              <p className="font-medium text-gray-800">Time Zone</p>
              <input
                type="text"
                ref={timeZoneRef}
                className={`mt-2 rounded-md py-2 ${
                  !editTimeZone ? "bg-purple-50" : "!bg-white"
                } w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500`}
                disabled={!editTimeZone}
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                aria-label="Time Zone"
              />
            </div>
            <button
              className="font-bold text-purple-800 hover:text-purple-500"
              onClick={handleTimeZoneEdit}
            >
              {editTimeZone ? "Save" : "Change"}
            </button>
          </div>
        </div>

        {/* Default Posting Times Section */}
        <div className="rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-6 shadow-md">
          <h4 className="text-lg font-semibold">Default Posting Times</h4>
          <div className="mt-6 space-y-6">
            {socialPlatforms.map((platform, index) => (
              <div
                key={index}
                className="flex justify-between border-b border-gray-200 pb-4 last:border-0 last:pb-0"
              >
                <div>
                  <p className="font-medium text-gray-800">{platform.name}</p>
                  <div className="mt-2 flex gap-4 text-gray-800">
                    {platformTimes[index].map((time, timeIndex) => (
                      <input
                        key={timeIndex}
                        type="text"
                        value={time}
                        disabled={!editMode[index]}
                        onChange={(e) =>
                          handleTimeChange(index, timeIndex, e.target.value)
                        }
                        className={`w-20 ${
                          editMode[index] ? "bg-white" : "bg-gray-50"
                        } rounded-md p-1`}
                      />
                    ))}
                  </div>
                </div>
                <button
                  className="font-bold text-purple-800 hover:text-purple-500"
                  onClick={() => toggleEditMode(index)}
                >
                  {editMode[index] ? "Save" : "Edit"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default PostScheduling;
